import { useState, useEffect, useCallback } from "react";
import { useContextSelector } from "use-context-selector";
import AuthContext from "../../context/AuthContext";
import BetSlipContext from "../../context/BetSlipContext";
import { isEmpty, inRange } from "lodash";
import useScrollToItem from "../../component/Scroll";
import { useLocation } from "react-router-dom";
import { isMobile } from "react-device-detect";

export const useBookmakerLogic = ({
  marketName,
  detail,
  totalMatched,
  typeGame,
  marketId,
}) => {
  const [localLimit, setLocalLimit] = useState({});
  const scrollToItem = useScrollToItem("#header");

  // Context values
  const user = useContextSelector(AuthContext, (ctx) => ctx.user);
  const profileData = useContextSelector(AuthContext, (ctx) => ctx.profileData);
  const oneclickActive = useContextSelector(
    AuthContext,
    (ctx) => ctx.oneclickActive,
  );
  const oneClickBid = useContextSelector(AuthContext, (ctx) => ctx.oneClickBid);
  const defaultStakeLimit = useContextSelector(
    AuthContext,
    (ctx) => ctx.defaultStakeLimit,
  );

  const setShowBetDialog = useContextSelector(
    BetSlipContext,
    (ctx) => ctx.setShowBetDialog,
  );
  const setLimit = useContextSelector(BetSlipContext, (ctx) => ctx.setLimit);
  const setBetSlipObject = useContextSelector(
    BetSlipContext,
    (ctx) => ctx.setBetSlipObject,
  );
  const setOneClickBetLoader = useContextSelector(
    BetSlipContext,
    (ctx) => ctx.setOneClickBetLoader,
  );
  const handelPlaceBookmaker = useContextSelector(
    BetSlipContext,
    (ctx) => ctx.handelPlaceBookmaker,
  );
  const setToastObject = useContextSelector(
    BetSlipContext,
    (ctx) => ctx.setToastObject,
  );
  const location = useLocation();
  let loc = location?.pathname?.split("/");
  // ✅ Calculate limit for bookmaker
  const handelLimit = useCallback(
    (marketName) => {
      let result = profileData?.matchSetting?.find(
        (res) =>
          res?.type === marketName && res?.sportType === detail?.gameType,
      );
      if (!isEmpty(result)) return result;

      let fallback = detail?.matchSetting.find(
        (res) => res?.type === "bookmaker",
      );
      return (
        fallback || {
          maxAmount: 1000,
          maxProfit: 10000,
          minAmount: 1,
          oddsLimit: 100,
        }
      );
    },
    [profileData, detail],
  );

  // ✅ Set local limit whenever inputs change
  useEffect(() => {
    if (isEmpty(detail)) return;

    const limit = handelLimit(marketName);

    const newLimit = defaultStakeLimit?.find(
      (res) =>
        res?.betfairId === detail.eventType &&
        res?.market === marketName &&
        inRange(totalMatched, res?.min, res?.max),
    );
    const findNewLimit = detail?.limit?.find(
      (res) => res?.type === "bookmaker",
    );

    if (!isEmpty(findNewLimit)) {
      setLocalLimit({
        ...limit,
        maxAmount: Math.min(
          Number(limit?.maxAmount),
          Number(findNewLimit?.maxAmount),
        ),
        oddsLimit: Math.min(
          Number(limit?.oddsLimit),
          Number(findNewLimit?.oddsLimit),
        ),
      });
    } else {
      setLocalLimit(
        isEmpty(newLimit) ? limit : { ...limit, maxAmount: newLimit?.stake },
      );
    }
  }, [marketName, totalMatched, defaultStakeLimit, detail]);

  // ✅ Bet handler
  const handelChange = useCallback(
    (odds, type, team, item, e) => {
      if (isEmpty(user)) {
        setToastObject({
          type: "warning",
          title: "Warning",
          message: "Please login to place your bet!",
          status: true,
        });
        return;
      }

      if (odds?.rt <= 0) return;

      setShowBetDialog(true);
      if (loc[1] != "match-detail-sky" && !isMobile) {
        scrollToItem(e.currentTarget);
      }

      const object = {
        odds: odds?.rt,
        teamName: team,
        betType: type,
        playType: typeGame,
        newMarketId: marketId,
        object: {
          ...item,
          ...odds,
          teamName: team,
          eventId: detail?.eventId,
          marketId: marketId,
          newMarketId: marketId,
        },
      };

      // if (oneclickActive) {
      //   setOneClickBetLoader("bookmaker");
      //   handelPlaceBookmaker(localLimit, { ...object, bidPrice: oneClickBid });
      // }

      setLimit(localLimit);
      setBetSlipObject((prev) => ({ ...prev, ...object }));
    },
    [
      user,
      oneclickActive,
      oneClickBid,
      localLimit,
      typeGame,
      marketId,
      detail,
      scrollToItem,
      setShowBetDialog,
      setOneClickBetLoader,
      handelPlaceBookmaker,
      setLimit,
      setBetSlipObject,
      setToastObject,
    ],
  );

  // ✅ Validation helpers
  const isRtValid = useCallback((obj, limit) => {
    const { rt } = obj || {};
    if (rt === undefined || rt === null || rt === "" || rt === 0) return true;
    if (typeof rt === "number" && limit?.oddsLimit !== undefined) {
      return rt >= limit.oddsLimit;
    }
    return false;
  }, []);

  const checkArrays = useCallback(
    (arr1, arr2, limit) => {
      const combined = [...(arr1 || []), ...(arr2 || [])];
      return combined.length > 0
        ? combined.every((obj) => isRtValid(obj, limit))
        : true;
    },
    [isRtValid],
  );

  // ✅ Favorite check
  const fav = !isEmpty(
    detail?.favourites?.find(
      (obj) =>
        detail?.bookmakerMarketId === obj?.favoriteMarketId &&
        obj?.favoriteType === "Bookmaker",
    ),
  );

  return {
    localLimit,
    handelChange,
    checkArrays,
    fav,
  };
};
